import React, { useCallback, useEffect, useMemo } from 'react';
import OrgComponent from '../components/OrgComponent';
import {
  lookupTeamParticipation,
  teamScores as lookupTeamScores,
  lookupOrgRubricScores,
  lookupQuestionScores,
  orgScoresQueryKey,
} from '../../../actions/org-actions';
import {
  selectTeamParticipationQuery,
  selectTeamScoresQuery,
  selectOrgRubricScoresQuery,
  selectQuestionScoresQuery,
} from 'js/store/reducers/queries';
import {
  isOrgRubricKey,
  rubricTeamsToTeamScores,
  rubricTeamsToQuestionScores,
} from 'js/utils/rubric-org-scores';
import { noop } from 'lodash';
import { connect } from 'react-redux';
import { notify } from 'js/actions/alert-actions';
import { sendReminders } from 'js/actions/assessment-actions';

function OrgAssessmentContainer({
  organization,
  assessmentHistorySummaries,
  loading,
  error,
  lookupTeamScores,
  lookupOrgRubricScores,
  orgId,
  assessmentKey,
  teamScoresQuery,
  orgRubricScoresQuery,
  workspace,
  history,
  match,
  lookupTeamParticipation,
  teamParticipationQuery,
  lookupQuestionScores,
  questionScoresQuery,
  sendReminders,
  notify,
  templates,
}) {
  const isRubric = useMemo(
    () => isOrgRubricKey(assessmentKey, assessmentHistorySummaries),
    [assessmentKey, assessmentHistorySummaries]
  );

  const teamScoresForDisplay = useMemo(() => {
    if (!isRubric) {
      return teamScoresQuery;
    }

    const teams = orgRubricScoresQuery.data?.teams;
    const document = orgRubricScoresQuery.data?.document;
    return {
      query: orgRubricScoresQuery.query,
      data: teams ? rubricTeamsToTeamScores(teams, document) : null,
    };
  }, [isRubric, teamScoresQuery, orgRubricScoresQuery]);

  const shouldLoadTeamScores =
    !isRubric &&
    !teamScoresQuery?.data &&
    !teamScoresQuery?.query?.error &&
    teamScoresQuery?.query?.isLoading !== true;

  const shouldLoadOrgRubricScores =
    isRubric &&
    !orgRubricScoresQuery?.data &&
    !orgRubricScoresQuery?.query?.error &&
    orgRubricScoresQuery?.query?.isLoading !== true;

  const shouldLoadTeamParticipation =
    !isRubric &&
    !teamParticipationQuery?.data &&
    !teamParticipationQuery?.query?.error &&
    teamParticipationQuery?.query?.isLoading !== true;

  const questionScoresForDisplay = useMemo(() => {
    if (!isRubric) {
      return questionScoresQuery;
    }

    const teams = orgRubricScoresQuery.data?.teams;
    const document = orgRubricScoresQuery.data?.document;
    return {
      query: orgRubricScoresQuery.query,
      data: teams ? rubricTeamsToQuestionScores(teams, document) : null,
    };
  }, [isRubric, questionScoresQuery, orgRubricScoresQuery]);

  const shouldLoadQuestionScores =
    !isRubric &&
    !questionScoresQuery?.data &&
    !questionScoresQuery?.query?.error &&
    questionScoresQuery?.query?.isLoading !== true;

  useEffect(() => {
    if (shouldLoadTeamScores) {
      lookupTeamScores(orgId, assessmentKey).catch(noop);
    }
  }, [lookupTeamScores, orgId, assessmentKey, shouldLoadTeamScores]);

  useEffect(() => {
    if (shouldLoadOrgRubricScores) {
      lookupOrgRubricScores(orgId, assessmentKey, {}).catch(noop);
    }
  }, [lookupOrgRubricScores, orgId, assessmentKey, shouldLoadOrgRubricScores]);

  useEffect(() => {
    if (shouldLoadTeamParticipation) {
      lookupTeamParticipation(orgId, assessmentKey).catch(noop);
    }
  }, [
    lookupTeamParticipation,
    orgId,
    assessmentKey,
    shouldLoadTeamParticipation,
  ]);

  useEffect(() => {
    if (shouldLoadQuestionScores) {
      lookupQuestionScores(orgId, assessmentKey, {}).catch(noop);
    }
  }, [lookupQuestionScores, orgId, assessmentKey, shouldLoadQuestionScores]);

  const handleSendReminders = useCallback(
    (assessmentIds, threshold) => {
      return (async () => {
        try {
          await sendReminders(assessmentIds, threshold);
          notify({
            type: 'success',
            message: 'Your reminders are on their way!',
          });
        } catch (e) {
          console.error('Failed to send reminders:', e);
          notify({
            type: 'danger',
            message: "Uh oh! Your reminders didn't get through",
          });
        }
      })();
    },
    [notify, sendReminders]
  );

  return (
    <OrgComponent
      workspace={workspace}
      organization={organization}
      assessmentHistorySummaries={assessmentHistorySummaries}
      loading={loading}
      error={error}
      teamScoresQuery={teamScoresForDisplay}
      history={history}
      assessmentKey={assessmentKey}
      isRubric={isRubric}
      orgRubricScoresQuery={orgRubricScoresQuery}
      teamParticipationQuery={teamParticipationQuery}
      questionScoresQuery={questionScoresForDisplay}
      match={match}
      onSendReminders={handleSendReminders}
      templates={templates}
    />
  );
}

export default connect(
  (store, params) => {
    const orgId = params.match.params.orgId;
    const assessmentKey = params.match.params.assessmentKey;
    return {
      orgId,
      assessmentKey,
      workspace: params.workspace,
      assessmentHistorySummaries: params.assessmentHistorySummaries ?? [],
      teamScoresQuery: selectTeamScoresQuery(
        store,
        `${orgId}-${assessmentKey}`
      ),
      orgRubricScoresQuery: selectOrgRubricScoresQuery(
        store,
        orgScoresQueryKey(orgId, assessmentKey)
      ),
      teamParticipationQuery: selectTeamParticipationQuery(
        store,
        `${orgId}-${assessmentKey}`
      ),
      questionScoresQuery: selectQuestionScoresQuery(
        store,
        orgScoresQueryKey(orgId, assessmentKey)
      ),
    };
  },
  {
    lookupTeamScores,
    lookupOrgRubricScores,
    lookupTeamParticipation,
    lookupQuestionScores,
    sendReminders,
    notify,
  }
)(OrgAssessmentContainer);
